import { useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { RANGE_PRESETS } from "@/lib/range-presets";
import {
  REFRESH_OPTIONS,
  DEFAULT_REFRESH_INTERVAL,
  setRefreshInterval,
} from "@/lib/auto-refresh";
import { ThemeToggle } from "@/components/theme";

export type DeviceSegment = "desktop" | "mobile" | "tablet" | null;

interface DashboardToolbarProps {
  presetId: string;
  onPresetChange: (presetId: string) => void;
  onRefresh: () => void;
  device: DeviceSegment;
  onDeviceChange: (device: DeviceSegment) => void;
}

const DEVICE_OPTIONS: Array<{ label: string; value: DeviceSegment }> = [
  { label: "All devices", value: null },
  { label: "Desktop", value: "desktop" },
  { label: "Mobile", value: "mobile" },
  { label: "Tablet", value: "tablet" },
];

export function DashboardToolbar({
  presetId,
  onPresetChange,
  onRefresh,
  device,
  onDeviceChange,
}: DashboardToolbarProps) {
  const queryClient = useQueryClient();
  const [autoRefresh, setAutoRefresh] = useState<number | false>(DEFAULT_REFRESH_INTERVAL);

  const onAutoRefreshChange = (raw: string) => {
    const value = raw === "off" ? false : Number(raw);
    setAutoRefresh(value);
    setRefreshInterval(value);
    // Kick one refetch so observers immediately reschedule on the new cadence.
    void queryClient.refetchQueries({ type: "active" });
  };

  return (
    <div className="flex flex-wrap items-center gap-2">
      <label className="flex items-center gap-2 text-xs text-fg-muted">
        <span className="font-medium uppercase tracking-wide">Range</span>
        <select
          value={presetId}
          onChange={(e) => onPresetChange(e.target.value)}
          aria-label="Time range preset"
          className="rounded-md border border-border-subtle bg-surface-card/70 px-2 py-1 text-sm text-fg-secondary outline-none ring-info/40 focus:ring-2"
        >
          {RANGE_PRESETS.map((preset) => (
            <option key={preset.id} value={preset.id}>
              {preset.label}
            </option>
          ))}
        </select>
      </label>
      <label className="flex items-center gap-2 text-xs text-fg-muted">
        <span className="font-medium uppercase tracking-wide">Segment</span>
        <select
          value={device ?? "all"}
          onChange={(e) => onDeviceChange(e.target.value === "all" ? null : (e.target.value as DeviceSegment))}
          aria-label="Device segment (GA4)"
          className="rounded-md border border-border-subtle bg-surface-card/70 px-2 py-1 text-sm text-fg-secondary outline-none ring-info/40 focus:ring-2"
        >
          {DEVICE_OPTIONS.map((opt) => (
            <option key={opt.label} value={opt.value ?? "all"}>
              {opt.label}
            </option>
          ))}
        </select>
      </label>
      <label className="flex items-center gap-2 text-xs text-fg-muted">
        <span className="font-medium uppercase tracking-wide">Auto-refresh</span>
        <select
          value={autoRefresh === false ? "off" : String(autoRefresh)}
          onChange={(e) => onAutoRefreshChange(e.target.value)}
          aria-label="Auto-refresh interval"
          className="rounded-md border border-border-subtle bg-surface-card/70 px-2 py-1 text-sm text-fg-secondary outline-none ring-info/40 focus:ring-2"
        >
          {REFRESH_OPTIONS.map((opt) => (
            <option key={opt.label} value={opt.value === false ? "off" : String(opt.value)}>
              {opt.label}
            </option>
          ))}
        </select>
      </label>
      <button
        type="button"
        onClick={onRefresh}
        aria-label="Refresh all metrics"
        className="inline-flex items-center gap-1.5 rounded-md border border-border-subtle bg-surface-card/70 px-3 py-1.5 text-sm text-fg-secondary transition-colors hover:bg-surface-raised/70 focus:outline-none focus:ring-2 focus:ring-info/40"
      >
        <svg
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
          aria-hidden="true"
          className="h-3.5 w-3.5"
        >
          <path d="M3 12a9 9 0 0 1 15.5-6.3L21 8" />
          <path d="M21 3v5h-5" />
          <path d="M21 12a9 9 0 0 1-15.5 6.3L3 16" />
          <path d="M3 21v-5h5" />
        </svg>
        Refresh
      </button>
      <ThemeToggle />
    </div>
  );
}
